import { z } from 'zod';
import { diceStateSchema, diceStatePartialSchema } from './DiceState.ts';
import { currentStateSchema, currentStatePartialSchema } from './CurrentState.ts';
import { bankStateSchema, bankStatePartialSchema } from './BankState.ts';
import { gameLogStatePartialSchema, gameLogStateSchema } from './GameLogState.ts';
import { mapStatePartialSchema, mapStateSchema } from './map-state/MapState.ts';
import { tradeStatePartialSchema, tradeStateSchema } from './trade-state/TradeState.ts';
import { mechanicShipStateSchema, mechanicShipStatePartialSchema } from './mechanic-states/MechanicShipState.ts';
import { mechanicRoadStateSchema, mechanicRoadStatePartialSchema } from './mechanic-states/MechanicRoadState.ts';
import { mechanicCityStateSchema, mechanicCityStatePartialSchema } from './mechanic-states/MechanicCityState.ts';
import { mechanicCityWallStateSchema, mechanicCityWallStatePartialSchema } from './mechanic-states/MechanicCityWallState.ts';
import { mechanicDevelopmentCardsStateSchema, mechanicDevelopmentCardsStatePartialSchema } from './mechanic-states/MechanicDevelopmentCardsState.ts';
import { mechanicLongestRoadStateSchema, mechanicLongestRoadStatePartialSchema } from './mechanic-states/MechanicLongestRoadState.ts';
import { mechanicLargestArmyStateSchema, mechanicLargestArmyStatePartialSchema } from './mechanic-states/MechanicLargestArmyState.ts';
import { mechanicRobberStateSchema, mechanicRobberStatePartialSchema } from './mechanic-states/MechanicRobberState.ts';
import { mechanicPirateStateSchema, mechanicPirateStatePartialSchema } from './mechanic-states/MechanicPirateState.ts';
import { mechanicEventDiceStateSchema, mechanicEventDiceStatePartialSchema } from './mechanic-states/MechanicEventDiceState.ts';
import { gameChatStatePartialSchema, gameChatStateSchema } from './GameChatState.ts';
import { mechanicSpecialBuildPhaseStateSchema, mechanicSpecialBuildPhaseStatePartialSchema } from './mechanic-states/MechanicSpecialBuildPhaseState.ts';
import { mechanicKnightStateSchema, mechanicKnightStatePartialSchema } from './mechanic-states/MechanicKnightState.ts';
import { mechanicProgressCardsStateSchema, mechanicProgressCardsStatePartialSchema } from './mechanic-states/MechanicProgressCardsState.ts';
import { mechanicIslandExplorationStateSchema, mechanicIslandExplorationStatePartialSchema } from './mechanic-states/MechanicIslandExplorationState.ts';
import { mechanicMerchantStateSchema, mechanicMerchantStatePartialSchema } from './mechanic-states/MechanicMerchantState.ts';
import { mechanicBarbarianInvasionStateSchema, mechanicBarbarianInvasionStatePartialSchema } from './mechanic-states/MechanicBarbarianInvasionState.ts';
import { mechanicCityImprovementsStateSchema, mechanicCityImprovementsStatePartialSchema } from './mechanic-states/MechanicCityImprovementsState.ts';
import { mechanicSettlementStateSchema, mechanicSettlementStatePartialSchema } from './mechanic-states/MechanicSettlementState.ts';
import { validateState } from './ValidateState.ts';
import { AT_LEAST_ONE_PROPERTY_MESSAGE } from './PartialStrictObjectSchema.ts';
import { playerStatesPartialSchema, playerStatesSchema } from './PlayerState.ts';
// experimentalMechanicState is intentionally excluded here because it should fail and never save to the DB.
export const gameStateSchema = z.object({
    diceState: diceStateSchema.readonly(),
    bankState: bankStateSchema.readonly(),
    mapState: mapStateSchema.readonly(),
    currentState: currentStateSchema.readonly(),
    tradeState: tradeStateSchema.readonly(),
    playerStates: playerStatesSchema.readonly(),
    gameLogState: gameLogStateSchema.readonly(),
    gameChatState: gameChatStateSchema.readonly(),
    mechanicDevelopmentCardsState: mechanicDevelopmentCardsStateSchema.exactOptional(),
    mechanicProgressCardsState: mechanicProgressCardsStateSchema.exactOptional(),
    mechanicSettlementState: mechanicSettlementStateSchema.exactOptional(),
    mechanicCityState: mechanicCityStateSchema.exactOptional(),
    mechanicCityWallState: mechanicCityWallStateSchema.exactOptional(),
    mechanicRoadState: mechanicRoadStateSchema.exactOptional(),
    mechanicShipState: mechanicShipStateSchema.exactOptional(),
    mechanicLongestRoadState: mechanicLongestRoadStateSchema.exactOptional(),
    mechanicLargestArmyState: mechanicLargestArmyStateSchema.exactOptional(),
    mechanicRobberState: mechanicRobberStateSchema.exactOptional(),
    mechanicPirateState: mechanicPirateStateSchema.exactOptional(),
    mechanicEventDiceState: mechanicEventDiceStateSchema.exactOptional(),
    mechanicSpecialBuildPhaseState: mechanicSpecialBuildPhaseStateSchema.exactOptional(),
    mechanicIslandExplorationState: mechanicIslandExplorationStateSchema.exactOptional(),
    mechanicKnightState: mechanicKnightStateSchema.exactOptional(),
    mechanicMerchantState: mechanicMerchantStateSchema.exactOptional(),
    mechanicBarbarianInvasionState: mechanicBarbarianInvasionStateSchema.exactOptional(),
    mechanicCityImprovementsState: mechanicCityImprovementsStateSchema.exactOptional()
}).strict();
export function validateGameState(data) {
    return validateState(gameStateSchema, data);
}
// experimentalMechanicState is omitted for the same reason as gameStateNewSchema.
export const gameStatePartialSchema = z.object({
    diceState: diceStatePartialSchema,
    bankState: bankStatePartialSchema,
    mapState: mapStatePartialSchema,
    currentState: currentStatePartialSchema,
    tradeState: tradeStatePartialSchema,
    playerStates: playerStatesPartialSchema,
    gameLogState: gameLogStatePartialSchema,
    gameChatState: gameChatStatePartialSchema,
    mechanicDevelopmentCardsState: mechanicDevelopmentCardsStatePartialSchema,
    mechanicProgressCardsState: mechanicProgressCardsStatePartialSchema,
    mechanicSettlementState: mechanicSettlementStatePartialSchema,
    mechanicCityState: mechanicCityStatePartialSchema,
    mechanicCityWallState: mechanicCityWallStatePartialSchema,
    mechanicRoadState: mechanicRoadStatePartialSchema,
    mechanicShipState: mechanicShipStatePartialSchema,
    mechanicLongestRoadState: mechanicLongestRoadStatePartialSchema,
    mechanicLargestArmyState: mechanicLargestArmyStatePartialSchema,
    mechanicRobberState: mechanicRobberStatePartialSchema,
    mechanicPirateState: mechanicPirateStatePartialSchema,
    mechanicEventDiceState: mechanicEventDiceStatePartialSchema,
    mechanicSpecialBuildPhaseState: mechanicSpecialBuildPhaseStatePartialSchema,
    mechanicIslandExplorationState: mechanicIslandExplorationStatePartialSchema,
    mechanicKnightState: mechanicKnightStatePartialSchema,
    mechanicMerchantState: mechanicMerchantStatePartialSchema,
    mechanicBarbarianInvasionState: mechanicBarbarianInvasionStatePartialSchema,
    mechanicCityImprovementsState: mechanicCityImprovementsStatePartialSchema
}).partial().strict().refine((obj)=>Object.keys(obj).length > 0, {
    message: AT_LEAST_ONE_PROPERTY_MESSAGE
});
export function validateGameStatePartial(data) {
    return validateState(gameStatePartialSchema, data);
}
